import { FC, ReactNode } from "react";
import { ImageContentPosition, ImageProps } from "expo-image";
import Svg, { LinearGradient, Rect, Stop } from "react-native-svg";

import { VnBox } from "@/components/VnBox";
import { VnImage } from "@/components/VnImage";
import { VnScrollView } from "@/components/VnScrollView";
import { OnboardingScreenType } from "@/domain/onboarding";

type PhotoCardStepFramePropsType = OnboardingScreenType & {
  image: ImageProps["source"];
  imagePosition: ImageContentPosition;
  children: ReactNode;
};

const CARD_TOP_SCREEN_RATIO = 0.4;
const CARD_CORNER_RADIUS = 48;

// Fades from dark to clear behind the header, so the white logo stays readable
// on light photos.
const TOP_SHADE_ID = "photoCardTopShade";

// Paints a photo across the top of the screen and a maroon card over the rest,
// down behind the footer controls (so the step needs `footerTone: "dark"`).
//
// The card scrolls up over the photo when its content doesn't fit, and is
// clipped at the header and footer so text never runs under them.
export const PhotoCardStepFrame: FC<PhotoCardStepFramePropsType> = ({
  width,
  height,
  headerHeight,
  footerHeight,
  image,
  imagePosition,
  children,
}) => {
  const cardTop = Math.max(height * CARD_TOP_SCREEN_RATIO, headerHeight);

  return (
    <VnBox style={{ width }} className="flex-1">
      <VnImage
        source={image}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          // Runs under the card's rounded corners so no gap shows beside them.
          height: cardTop + CARD_CORNER_RADIUS,
        }}
        contentFit="cover"
        contentPosition={imagePosition}
        accessible={false}
      />
      <VnBox
        className="absolute left-0 right-0 top-0"
        style={{ height: headerHeight * 1.5 }}
        pointerEvents="none">
        <Svg width="100%" height="100%">
          <LinearGradient id={TOP_SHADE_ID} x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="black" stopOpacity={0.45} />
            <Stop offset="0.5" stopColor="black" stopOpacity={0.18} />
            <Stop offset="1" stopColor="black" stopOpacity={0} />
          </LinearGradient>
          <Rect width="100%" height="100%" fill={`url(#${TOP_SHADE_ID})`} />
        </Svg>
      </VnBox>

      <VnScrollView
        className="flex-1"
        style={{ marginTop: headerHeight }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: cardTop - headerHeight,
        }}>
        <VnBox
          className="bg-sidebar flex-1 items-center px-6 pb-6 pt-10"
          style={{
            borderTopLeftRadius: CARD_CORNER_RADIUS,
            borderTopRightRadius: CARD_CORNER_RADIUS,
          }}>
          <VnBox className="w-full gap-5 md:max-w-screen-sm">{children}</VnBox>
        </VnBox>
      </VnScrollView>
      <VnBox className="bg-sidebar" style={{ height: footerHeight }} />
    </VnBox>
  );
};
